import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  Button,
  Grid,
  IconButton,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import * as actions from "./actions";
import moment from "moment";
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import sampleB2BFile from '../../assets/sample.docx';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import _ from "lodash";
import CustomAddModal from "../Common/CustomAddModal";
import { toastSuccess, toastError } from "../../utils";
import { API_URL } from '../../constants';

// Styled Components
const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const PageTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#F1F5F9',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const StatsChip = styled(Chip)({
  background: 'linear-gradient(135deg, rgba(18, 180, 161, 0.15) 0%, rgba(5, 150, 179, 0.15) 100%)',
  border: '1px solid rgba(18, 180, 161, 0.3)',
  color: '#12B4A1',
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 32,
  '& .MuiChip-label': {
    padding: '0 12px',
  },
});

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderRadius: 12,
    color: '#F1F5F9',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(18, 180, 161, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#12B4A1',
      borderWidth: 2,
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#64748B',
  },
  '& input::placeholder': {
    color: '#64748B',
    opacity: 1,
  },
});

const GlassCard = styled(Paper)({
  borderRadius: 16,
  background: 'rgba(30, 41, 59, 0.5)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
});

const StyledTableContainer = styled(TableContainer)({
  background: 'transparent',
  boxShadow: 'none',
  border: 'none',
  '&.MuiPaper-root': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },
});

const StyledTableHead = styled(TableHead)({
  '& .MuiTableCell-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
  },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.05)',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    color: '#E2E8F0',
    padding: '12px 16px',
  },
}));

const EmptyState = styled(Box)({
  textAlign: 'center',
  padding: '48px 24px',
  color: '#64748B',
});

const AddButton = styled(Button)({
  background: 'linear-gradient(135deg, #12B4A1 0%, #0596B3 100%)',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 10,
  padding: '8px 20px',
  '&:hover': {
    background: 'linear-gradient(135deg, #0fa392 0%, #0487a1 100%)',
  },
});

const FormTextField = styled(TextField)({
  marginBottom: 12,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(18, 180, 161, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#12B4A1',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-input': {
    color: '#E2E8F0',
    padding: '10px 14px',
    fontSize: '0.875rem',
  },
});

const FileUploadButton = styled(Button)({
  marginBottom: 12,
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px dashed rgba(255, 255, 255, 0.2)',
  color: '#94A3B8',
  textTransform: 'none',
  padding: '12px 16px',
  justifyContent: 'flex-start',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(18, 180, 161, 0.5)',
  },
});

const StatusChip = styled(Chip)(({ statustype }) => {
  const colors = {
    active: { bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.3)', text: '#22C55E' },
    verified: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.3)', text: '#3B82F6' },
    pending: { bg: 'rgba(251, 191, 36, 0.15)', border: 'rgba(251, 191, 36, 0.3)', text: '#FBBF24' },
    rejected: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: '#EF4444' },
  };
  const color = colors[statustype] || colors.pending;
  return {
    background: color.bg,
    border: `1px solid ${color.border}`,
    color: color.text,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 26,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-icon': {
      color: color.text,
      fontSize: 14,
    },
  };
});

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  password: '',
};

export default function SubLabels(props) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
  });
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');

  // Modal state
  const [openAddModal, setOpenAddModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [b2bFile, setB2bFile] = useState(null);
  const [b2bFileName, setB2bFileName] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onGetSubLabels = useCallback((filters) => {
    props.setLoader(true);
    actions.getSubLabelsAdmin(filters).then((res) => {
      props.setLoader(false);
      setItems(res.body);
      setCount(res.total);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const getSearchSubLabels = useCallback((filters) => {
    props.setLoader(true);
    actions.searchSubLabelsAdmin(filters).then((res) => {
      props.setLoader(false);
      setItems(res.body);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const delaySearch = useMemo(
    () => _.debounce((filters) => getSearchSubLabels(filters), 500),
    [getSearchSubLabels]
  );

  useEffect(() => {
    if (!search) {
      onGetSubLabels(filters);
    }
  }, [filters.limit, filters.offset, filters.page]);

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    setFilters({ ...filters, page, offset });
  };

  const searchSubLabels = (value) => {
    setSearch(value);
    if (!value) {
      onGetSubLabels(filters);
    } else {
      delaySearch({ search: value });
    }
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!b2bFileName) errors.b2bFile = 'B2B file is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setB2bFile(file);
      if (formErrors.b2bFile) {
        setFormErrors({ ...formErrors, b2bFile: '' });
      }

      // Upload immediately
      setUploading(true);
      try {
        const fileFormData = new FormData();
        fileFormData.append('photo', file);
        const uploadResult = await actions.b2bFileUploadAdmin(fileFormData);

        if (uploadResult.status === 200) {
          setB2bFileName(uploadResult.body?.filename);
          toastSuccess('File uploaded successfully');
        } else {
          toastError('Failed to upload file');
          setB2bFile(null);
        }
      } catch (err) {
        toastError(err.message || 'Failed to upload file');
        setB2bFile(null);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleOpenModal = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setB2bFile(null);
    setB2bFileName(null);
    setOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setFormData(initialFormData);
    setFormErrors({});
    setB2bFile(null);
    setB2bFileName(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      // Check if email exists
      const emailCheck = await actions.checkEmailExistsAdmin(formData.email);
      if (emailCheck.body?.exists) {
        const reason = emailCheck.body?.reason;
        const errorMsg = reason === 'invited' ? 'Email already invited' : 'Email already registered';
        setFormErrors({ ...formErrors, email: errorMsg });
        setSubmitting(false);
        return;
      }

      // Submit the form with full URL for b2b_file
      const result = await actions.addSubLabelAdmin({
        ...formData,
        b2b_file: `${API_URL}/${b2bFileName}`
      });
      toastSuccess(result.message || 'Sub label added successfully');
      handleCloseModal();
      onGetSubLabels(filters); // Refresh the list
    } catch (err) {
      toastError(err.message || 'Failed to add sub label');
    } finally {
      setSubmitting(false);
    }
  };

  const getReviewStatusInfo = (item) => {
    if (item.user_type === 'active') {
      return { label: 'Active', type: 'active' };
    }
    if (item.status === 'in_review') {
      return { label: 'In Review', type: 'verified' };
    }
    if (item.status === 'rejected') {
      return { label: 'Rejected', type: 'rejected' };
    }
    return { label: item.status || 'Pending', type: 'pending' };
  };

  let totalPages = Math.ceil(Number(count) / 50);
  if (search) totalPages = 1;

  return (
    <Box>
      <Helmet>
        <title>Sub Labels - Fronic Media Labels</title>
      </Helmet>
      <PageHeader>
        <PageTitle>
          <PeopleIcon sx={{ color: '#12B4A1', fontSize: 28 }} />
          Sub Labels
        </PageTitle>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <StatsChip label={`Total: ${count}`} />
          <AddButton
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Add Sub Label
          </AddButton>
        </Box>
      </PageHeader>

      <Box sx={{ mb: 3 }}>
        <SearchField
          size="small"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={(e) => searchSubLabels(e.target.value)}
          sx={{ width: '50%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <GlassCard elevation={0}>
        <StyledTableContainer component={Paper} elevation={0}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User Since</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item, key) => {
                  const reviewStatusInfo = getReviewStatusInfo(item);
                  return (
                    <StyledTableRow key={item.review_id || item.user_id || key}>
                      <TableCell>
                        <Typography sx={{ color: '#64748B', fontSize: '0.875rem' }}>
                          {(filters.page - 1) * filters.limit + (key + 1)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, color: '#E2E8F0', fontSize: '0.875rem' }}>
                          {item.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                          {item.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                          {item.phone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          statustype={reviewStatusInfo.type}
                          label={reviewStatusInfo.label}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                          {item.user_type === 'active' && item.created_at ? moment(item.created_at).format('MMM DD, YYYY') : '-'}
                        </Typography>
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState>
                      <PeopleIcon sx={{ fontSize: 48, color: '#334155', mb: 2 }} />
                      <Typography variant="body1" sx={{ color: '#64748B' }}>
                        No sub labels found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
                        Try adjusting your search criteria
                      </Typography>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {totalPages > 1 && (
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
            />
          </Box>
        )}
      </GlassCard>

      {/* Add New Sub Label Modal */}
      <CustomAddModal
        open={openAddModal}
        handleClose={handleCloseModal}
        title="Add New Sub Label"
        saveLabel={submitting ? "Creating..." : "Create Sub Label"}
        onSubmitConfirm={handleSubmit}
        disableSave={submitting || uploading}
        saveButtonMuted={!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.address.trim() || !formData.password.trim() || !b2bFileName}
        hideCloseButton
        maxWidth="sm"
      >
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                required
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                placeholder="Enter full name"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                placeholder="Enter email address"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                required
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                placeholder="Enter phone number"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                required
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formData.address}
                onChange={handleInputChange}
                error={!!formErrors.address}
                helperText={formErrors.address}
                placeholder="Enter address"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                fullWidth
                required
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                placeholder="Enter password"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                  Need a template?
                </Typography>
                <Button
                  component="a"
                  href={sampleB2BFile}
                  download="B2B_Sample_Template.docx"
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    color: '#60A5FA',
                    padding: '2px 8px',
                    minWidth: 'auto',
                    '&:hover': {
                      background: 'rgba(96, 165, 250, 0.1)',
                    },
                  }}
                >
                  Download Sample
                </Button>
              </Box>
              <input
                type="file"
                id="b2b-file-input"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                disabled={uploading}
              />
              <label htmlFor="b2b-file-input">
                <FileUploadButton
                  component="span"
                  fullWidth
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={16} sx={{ color: '#FBBF24' }} /> : b2bFileName ? <CheckCircleIcon sx={{ color: '#22C55E' }} /> : <AttachFileIcon />}
                  sx={{
                    borderColor: formErrors.b2bFile ? '#f44336' : b2bFileName ? 'rgba(34, 197, 94, 0.3)' : undefined,
                    background: b2bFileName ? 'rgba(34, 197, 94, 0.1)' : undefined,
                  }}
                >
                  {uploading ? 'Uploading...' : b2bFile ? b2bFile.name : 'Attach B2B File (PDF/DOC) *'}
                </FileUploadButton>
              </label>
              {b2bFileName && !uploading && (
                <Typography variant="caption" sx={{ color: '#22C55E', ml: 1.5 }}>
                  Uploaded - Click to replace
                </Typography>
              )}
              {formErrors.b2bFile && (
                <Typography variant="caption" sx={{ color: '#f44336', ml: 1.5 }}>
                  {formErrors.b2bFile}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: '#94A3B8', mt: 2, display: 'block' }}>
            Sub label will be created and sent for review.
          </Typography>
        </Box>
      </CustomAddModal>
    </Box>
  );
}
